import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, BookOpen, LogIn, LayoutDashboard, CalendarRange, PlayCircle,
  UserPlus, Users2, ShieldAlert, Users, BarChart3, Award, FileText,
  Bell, Building2, ClipboardCheck, CreditCard, Settings, GraduationCap,
  ServerCog, HelpCircle, CheckCircle2, Lightbulb, ChevronRight,
} from "lucide-react";

const Step = ({ children }) => (
  <li className="flex items-start gap-2.5">
    <ChevronRight className="w-4 h-4 text-sky-600 mt-0.5 shrink-0" />
    <span>{children}</span>
  </li>
);

const Tip = ({ children }) => (
  <div className="mt-4 flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
    <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
    <p className="text-sm text-amber-900 leading-relaxed m-0">{children}</p>
  </div>
);

const Tag = ({ children, tone }) => {
  const tones = {
    common: "bg-slate-100 text-slate-600",
    admin: "bg-sky-100 text-sky-700",
    staff: "bg-emerald-100 text-emerald-700",
    ops: "bg-amber-100 text-amber-700",
  };
  return <span className={`text-[11px] font-bold rounded-full px-2.5 py-1 ${tones[tone] || tones.common}`}>{children}</span>;
};

const SECTIONS = [
  {
    id: "intro", label: "はじめに", icon: BookOpen, tone: "common", tagLabel: "共通",
    content: (
      <>
        <p>Welfy Care Campus（WCC）は、障がい福祉サービス事業者のための「研修・法定管理」クラウドです。動画eラーニング、年間計画、委員会・訓練の記録、修了証の自動発行、実地指導（監査）対応までを、ひとつの画面で完結できます。</p>
        <p>本ガイドでは、はじめて利用される方に向けて、主な機能の使い方を役割ごとにご案内します。左の目次から知りたい項目へ移動できます。</p>
        <ul className="mt-4 space-y-2">
          <Step><b>法人管理者</b>：法人全体の設定・事業所・請求を管理します。</Step>
          <Step><b>事業所管理者</b>：担当事業所の研修配信・職員・記録を管理します。</Step>
          <Step><b>職員（受講者）</b>：割り当てられた研修を受講し、修了証を確認します。</Step>
        </ul>
      </>
    ),
  },
  {
    id: "login", label: "ログインと基本操作", icon: LogIn, tone: "common", tagLabel: "共通",
    content: (
      <>
        <ul className="space-y-2">
          <Step>トップページ右上の「ログイン」から、発行されたログインIDとパスワードでログインします。</Step>
          <Step>画面左のメニューから各機能へ移動します（スマートフォンは左上のメニューボタン）。</Step>
          <Step>画面右上で「年度」を切り替えると、その年度のデータが表示されます。</Step>
          <Step>右上のユーザー名メニューから「設定」やパスワード変更、ログアウトができます。</Step>
        </ul>
        <Tip>パスワードを忘れた場合や、ログインができない場合は、法人管理者または運営事務局へお問い合わせください。</Tip>
      </>
    ),
  },
  {
    id: "dashboard", label: "ホーム・ダッシュボード", icon: LayoutDashboard, tone: "common", tagLabel: "共通",
    content: (
      <>
        <p>ログイン後の最初の画面です。役割によって表示が変わります。</p>
        <ul className="mt-3 space-y-2">
          <Step><b>管理者</b>：研修の実施状況、修了率、期限が近い研修、未対応の項目などをひと目で確認できます。</Step>
          <Step><b>職員</b>：自分が受講すべき研修と期限、進捗が一覧で表示されます。</Step>
        </ul>
      </>
    ),
  },
  {
    id: "annual-plan", label: "年間計画", icon: CalendarRange, tone: "admin", tagLabel: "管理者",
    content: (
      <>
        <p>年間の研修・委員会・訓練の実施予定を、月次グリッドで計画・記録します。実地指導で求められる「年間研修計画」として出力できます。</p>
        <ul className="mt-3 space-y-2">
          <Step>サービス種別のテンプレートから、法定研修・委員会・訓練をまとめて追加できます。</Step>
          <Step>各項目に実施月を設定し、実施したら記録を残します。</Step>
          <Step>作成した計画は帳票として印刷・PDF出力できます。</Step>
        </ul>
      </>
    ),
  },
  {
    id: "trainings", label: "研修（配信・受講）", icon: PlayCircle, tone: "common", tagLabel: "共通",
    content: (
      <>
        <p className="font-semibold text-slate-800">管理者の操作</p>
        <ul className="mt-2 space-y-2">
          <Step>「研修」からマスター教材を選び、研修を追加します。</Step>
          <Step>対象の職員・事業所を選んで配信し、受講期限を設定します。</Step>
          <Step>動画チャプター・理解度確認テスト（合格基準）を設定できます。</Step>
        </ul>
        <p className="font-semibold text-slate-800 mt-5">職員の操作</p>
        <ul className="mt-2 space-y-2">
          <Step>割り当てられた研修を開き、動画を視聴します（続きから再生できます）。</Step>
          <Step>視聴後に理解度確認テストを受け、合格すると自動で修了となります。</Step>
        </ul>
        <Tip>受講状況・視聴履歴は自動で記録され、実地指導の証拠として残ります。</Tip>
      </>
    ),
  },
  {
    id: "onboarding", label: "入社研修", icon: UserPlus, tone: "common", tagLabel: "共通",
    content: (
      <>
        <p>新規採用職員向けの初期研修を管理します。入社時に必要な研修一式を割り当て、受講状況を追跡できます。「入社研修設定」で標準の研修メニューをあらかじめ用意しておくと、新入職員に自動で割り当てられます。</p>
      </>
    ),
  },
  {
    id: "committees", label: "委員会・議事録", icon: Users2, tone: "admin", tagLabel: "管理者",
    content: (
      <>
        <ul className="space-y-2">
          <Step>虐待防止・身体拘束適正化などの委員会を登録し、開催記録・議事録を残します。</Step>
          <Step>議事録を職員へ周知し、「確認しました」の記録まで一元管理できます。</Step>
          <Step>議事録・開催記録はPDF帳票として出力できます。</Step>
        </ul>
      </>
    ),
  },
  {
    id: "drills", label: "訓練（避難・防災など）", icon: ShieldAlert, tone: "admin", tagLabel: "管理者",
    content: (
      <>
        <p>避難訓練・消火訓練・不審者対応などの実施記録を管理します。実施日・参加者・内容・振り返りを記録し、訓練記録としてPDF出力できます。</p>
      </>
    ),
  },
  {
    id: "staff", label: "職員管理", icon: Users, tone: "admin", tagLabel: "管理者",
    content: (
      <>
        <ul className="space-y-2">
          <Step>職員の登録・編集・所属事業所の設定を行います。</Step>
          <Step>CSVによる一括登録に対応しています。</Step>
          <Step>役割（法人管理者／事業所管理者／職員）を設定し、アクセス範囲を制御します。</Step>
        </ul>
        <Tip>事業所管理者には担当事業所の職員のみが表示されます（事業所単位のデータ分離）。</Tip>
      </>
    ),
  },
  {
    id: "progress", label: "進捗管理", icon: BarChart3, tone: "admin", tagLabel: "管理者",
    content: (
      <>
        <p>研修ごと・職員ごとの受講状況と修了率を一覧で確認できます。未受講・期限超過の職員を絞り込み、リマインドの対象を把握できます。</p>
      </>
    ),
  },
  {
    id: "certificates", label: "修了証", icon: Award, tone: "common", tagLabel: "共通",
    content: (
      <>
        <ul className="space-y-2">
          <Step>研修を修了すると、日本語PDFの修了証が自動で発行されます。</Step>
          <Step>「誰が・いつ・何を」修了したかが証拠として残ります。</Step>
          <Step>管理者は修了証の一覧を絞り込み、印刷・PDF出力ができます。</Step>
          <Step>各修了証には検証用QRコードが付き、真正性を確認できます。</Step>
        </ul>
        <Tip>修了証のレイアウト・印影（社印）・代表者名などは運営コンソールで設定できます。</Tip>
      </>
    ),
  },
  {
    id: "reports", label: "帳票出力", icon: FileText, tone: "admin", tagLabel: "管理者",
    content: (
      <>
        <p>実地指導・監査に必要な各種帳票をPDFで出力します。研修実施記録、年間計画、委員会議事録、訓練記録などをワンクリックで作成できます。</p>
      </>
    ),
  },
  {
    id: "notifications", label: "通知・メール・配信レポート", icon: Bell, tone: "common", tagLabel: "共通",
    content: (
      <>
        <ul className="space-y-2">
          <Step><b>通知</b>：受講期限のリマインドやお知らせが表示されます。</Step>
          <Step><b>メール</b>：サイト内メッセージ機能で職員へ連絡できます（未読バッジ付き）。</Step>
          <Step><b>配信レポート</b>：送信した通知・メールの到達状況を確認できます。</Step>
        </ul>
        <Tip>LINE・WhatsApp連携（無料枠）や受講期限の自動リマインドにも対応しています。</Tip>
      </>
    ),
  },
  {
    id: "offices", label: "事業所管理", icon: Building2, tone: "admin", tagLabel: "法人管理者",
    content: (
      <>
        <p>法人配下の事業所を登録・管理します。事業所ごとにサービス種別を設定すると、その種別に応じた法定研修テンプレートが利用できます。</p>
      </>
    ),
  },
  {
    id: "audit", label: "実地指導・操作履歴", icon: ClipboardCheck, tone: "admin", tagLabel: "管理者",
    content: (
      <>
        <ul className="space-y-2">
          <Step><b>実地指導モード</b>：準備状況を緑／黄／赤で可視化し、抜け漏れを事前に確認できます。実地指導レポートをPDF出力できます。</Step>
          <Step><b>操作履歴（監査ログ）</b>：いつ・誰が・どの操作を行ったかを記録し、後から確認できます。</Step>
        </ul>
      </>
    ),
  },
  {
    id: "billing", label: "契約・請求", icon: CreditCard, tone: "admin", tagLabel: "法人管理者",
    content: (
      <>
        <p>ご契約中のプラン・請求状況を確認できます。プラン変更やお支払いに関する手続きが行えます。</p>
      </>
    ),
  },
  {
    id: "settings", label: "設定", icon: Settings, tone: "admin", tagLabel: "法人管理者",
    content: (
      <>
        <p>法人情報、通知の設定、印刷設定などを管理します。修了証や帳票に表示される法人名・代表者名などもこちらで確認・設定できます。</p>
      </>
    ),
  },
  {
    id: "staff-guide", label: "職員（受講者）の方へ", icon: GraduationCap, tone: "staff", tagLabel: "職員",
    content: (
      <>
        <p>職員の方は、次の3ステップだけ覚えておけば大丈夫です。</p>
        <ol className="mt-3 space-y-3">
          <li className="flex items-start gap-3"><span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-sm font-bold flex items-center justify-center shrink-0">1</span><span><b>ホーム</b>で、自分に割り当てられた研修と期限を確認します。</span></li>
          <li className="flex items-start gap-3"><span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-sm font-bold flex items-center justify-center shrink-0">2</span><span>研修を開いて<b>動画を視聴</b>し、<b>理解度確認テスト</b>に合格します。</span></li>
          <li className="flex items-start gap-3"><span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-sm font-bold flex items-center justify-center shrink-0">3</span><span><b>修了証</b>から、発行された修了証を確認・保存できます。</span></li>
        </ol>
        <Tip>受講は途中で中断しても、次回「続きから」再生できます。期限前には通知でお知らせします。</Tip>
      </>
    ),
  },
  {
    id: "ops", label: "運営コンソール（運営者向け）", icon: ServerCog, tone: "ops", tagLabel: "運営",
    content: (
      <>
        <p>運営（スーパー管理者）は、専用の運営コンソールから全法人の管理を行います。</p>
        <ul className="mt-3 space-y-2">
          <Step><b>法人管理</b>：契約法人の登録・状態管理を行います。</Step>
          <Step><b>プラン・料金</b>：提供プランと価格を設定します。</Step>
          <Step><b>研修コンテンツ</b>：全法人が利用するマスター教材を管理します。</Step>
          <Step><b>年間計画設定</b>：サービス種別ごとの標準テンプレートを整備します。</Step>
          <Step><b>修了証設定</b>：修了証のレイアウト・印影・代表者名などを設定します。</Step>
          <Step><b>LP設定</b>：トップページ（ランディングページ）の内容を編集します。</Step>
          <Step><b>お問い合わせ</b>：トップページから届いた問い合わせを確認します。</Step>
        </ul>
      </>
    ),
  },
  {
    id: "faq", label: "よくある質問", icon: HelpCircle, tone: "common", tagLabel: "共通",
    content: (
      <>
        <div className="space-y-4">
          <div>
            <p className="font-semibold text-slate-800 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-sky-600" />受講期限を過ぎてしまいました。</p>
            <p className="mt-1 text-slate-600">期限を過ぎても受講は可能です。速やかに受講を完了してください。管理者は期限超過の職員を進捗管理から確認できます。</p>
          </div>
          <div>
            <p className="font-semibold text-slate-800 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-sky-600" />修了証が発行されません。</p>
            <p className="mt-1 text-slate-600">研修の修了条件（動画視聴・テスト合格）を満たしているかご確認ください。すべて満たすと自動で発行されます。</p>
          </div>
          <div>
            <p className="font-semibold text-slate-800 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-sky-600" />職員を一括で登録したい。</p>
            <p className="mt-1 text-slate-600">「職員管理」からCSVによる一括登録が可能です。</p>
          </div>
          <div>
            <p className="font-semibold text-slate-800 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-sky-600" />操作に困ったときは？</p>
            <p className="mt-1 text-slate-600">トップページのお問い合わせフォーム、または運営事務局へご連絡ください。</p>
          </div>
        </div>
      </>
    ),
  },
];

export default function UserGuide() {
  const navigate = useNavigate();
  const [active, setActive] = useState(SECTIONS[0].id);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] text-slate-900 font-sans" data-testid="user-guide-page">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/wcc-icon.png" alt="WCC" className="w-9 h-9 rounded-xl object-contain shadow-sm shrink-0" />
            <div className="leading-tight min-w-0">
              <div className="font-serif text-base sm:text-lg font-bold tracking-tight truncate">使い方ガイド</div>
              <div className="text-[10px] text-slate-400 truncate">Welfy Care Campus 操作マニュアル</div>
            </div>
          </div>
          <button
            onClick={() => navigate(-1)}
            data-testid="guide-back-btn"
            className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-sky-700 rounded-full border border-slate-300 px-4 py-2 hover:bg-white transition-colors shrink-0"
          >
            <ArrowLeft className="w-4 h-4" /> 戻る
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 lg:py-12 grid lg:grid-cols-[240px_1fr] gap-8 lg:gap-12">
        {/* TOC */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="text-xs font-bold tracking-[0.15em] uppercase text-slate-400 mb-3 px-3">目次</div>
          <nav className="space-y-0.5 max-h-[70vh] overflow-y-auto pr-1" data-testid="guide-toc">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                data-testid={`guide-toc-${s.id}`}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                  active === s.id ? "bg-sky-50 text-sky-700 font-semibold" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <s.icon className="w-4 h-4 shrink-0" />
                <span className="flex-1 truncate">{s.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <main ref={containerRef} className="min-w-0 space-y-10">
          <div className="rounded-3xl bg-gradient-to-br from-sky-700 to-sky-500 text-white p-8 lg:p-10">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-sky-100">User Guide</span>
            <h1 className="mt-3 text-3xl sm:text-4xl font-serif font-bold tracking-tight leading-tight">はじめての方へ<br className="sm:hidden" />：WCCの使い方</h1>
            <p className="mt-4 text-sky-50 leading-relaxed max-w-xl text-sm sm:text-base">
              研修の配信・受講から、修了証の発行、実地指導への備えまで。役割ごとの基本操作をまとめました。左の目次から知りたい項目へお進みください。
            </p>
          </div>

          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-24" data-testid={`guide-section-${s.id}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-2xl bg-sky-50 flex items-center justify-center shrink-0">
                  <s.icon className="w-5 h-5 text-sky-700" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 font-sans flex-1">{s.label}</h2>
                <Tag tone={s.tone}>{s.tagLabel}</Tag>
              </div>
              <div className="prose-guide text-slate-700 leading-relaxed text-sm sm:text-[15px] space-y-3 [&_p]:m-0 pl-1">
                {s.content}
              </div>
            </section>
          ))}

          <div className="border-t border-slate-200 pt-8 text-center">
            <p className="text-sm text-slate-500">ご不明な点は、トップページのお問い合わせフォームまたは運営事務局までご連絡ください。</p>
            <button onClick={() => navigate(-1)} data-testid="guide-back-bottom" className="mt-4 inline-flex items-center gap-1.5 text-sm text-sky-700 hover:text-sky-600 font-medium">
              <ArrowLeft className="w-4 h-4" /> 前の画面に戻る
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}
